/**
 * The pool settings pane, addressed at `/pools?poolId=<id>&view=settings`.
 * `POST /pool/get` is the single read that drives it: the
 * loading skeletons, the failed retry banner, the inline no-access state,
 * and the card slots.
 *
 * Eight of the ten §27.2 slots are filled and those eight are the whole
 * screen: `pool` (ISSUE-201), `joining` (ISSUE-192), `contest-rules`
 * (ISSUE-200), `contests` (ISSUE-1042), `season` (ISSUE-501), `awards`
 * (ISSUE-482), `chat` (ISSUE-1040) and `danger-zone` (ISSUE-817). The other
 * two are reserved and unbuilt, and `visibleSlotsForRole` drops them rather
 * than rendering two headers that do nothing when tapped. The
 * branch below and `hasContent` in `poolSettingsState.ts` are two halves of
 * one fact and change together.
 */
import { usePoolGet } from "@sportsmet/pool-sdk/private";
import { InlineBanner, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useEffect } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import { isPoolNoAccessCode } from "../../errors/poolAccess";
import { PoolAwardsPanel } from "../poolAwards/PoolAwardsPanel";
import { PoolSeasonCardContainer } from "../poolSeason/PoolSeasonCardContainer";
import type { typeJoiningPoolInput } from "./joiningCardState";
import { PoolSettingsCardSlot } from "./PoolSettingsCardSlot";
import { PoolSettingsChatCard } from "./PoolSettingsChatCard";
import { PoolSettingsContestsCard } from "./PoolSettingsContestsCard";
import { PoolSettingsDangerZoneCard } from "./PoolSettingsDangerZoneCard";
import { PoolSettingsJoiningCard } from "./PoolSettingsJoiningCard";
import { PoolSettingsNoAccess } from "./PoolSettingsNoAccess";
import { PoolSettingsPoolCard } from "./PoolSettingsPoolCard";
import type { typePoolSettingsPanel } from "./poolSettingsRoute";
import { PoolSettingsRulesCard } from "./PoolSettingsRulesCard";
import {
  POOL_SETTINGS_BUILT_SLOTS,
  POOL_SETTINGS_FAILED_MESSAGE,
  POOL_SETTINGS_LOADING_LABEL,
  POOL_SETTINGS_RETRY_LABEL,
  resolvePoolSettingsState,
} from "./poolSettingsState";

const SURVIVOR_ONLY_SETTINGS_PANELS: ReadonlySet<string> = new Set([
  "contest-rules",
  "contests",
  "season",
]);

export type typePoolSettingsPaneProps = {
  poolId: string;
  activePanel: typePoolSettingsPanel | null;
  /** Only the inline no-access card uses this. The Settings tab passes none: the pool header's back arrow is the one way out of a pool (ISSUE-1035). */
  onGoBack?: (() => void) | undefined;
};

export const PoolSettingsPane = ({
  poolId,
  activePanel,
  onGoBack,
}: typePoolSettingsPaneProps): ReactNode => {
  const pool = usePoolGet({ poolId });

  useEffect(() => {
    const code = extractErrorCode(pool.data);
    if (code !== null && !isPoolNoAccessCode(code)) {
      showApiError(pool.data, undefined, pool.dataUpdatedAt);
    }
  }, [pool.dataUpdatedAt, poolId]);

  const state = resolvePoolSettingsState({
    isLoading: pool.isLoading,
    isTransportError: pool.isError,
    errorCode: extractErrorCode(pool.data),
    poolName: pool.data?.data?.pool.name ?? null,
    viewerPoolRole: pool.data?.data?.viewerPoolRole ?? null,
  });
  const poolFields: typeJoiningPoolInput = pool.data?.data?.pool ?? {};
  // ISSUE-1334. Contest rules, Contests and Season are Survivor's cards. A
  // Squares or Strip Card pool brings its own settings section instead
  // (PoolSettingsTabPane), so those three are dropped for it.
  const contestType = pool.data?.data?.pool.contestType ?? "survivor";
  // ISSUE-1429. A Pick'em pool keeps the Contests card: its contests follow
  // one another in a season the way Survivor's do, and New contest there
  // opens the Pick'em dialog.
  const slots =
    state.phase === "ready"
      ? state.slots.filter(
          (slot) =>
            contestType === "survivor" ||
            (contestType === "pickem" && slot.panel === "contests") ||
            !SURVIVOR_ONLY_SETTINGS_PANELS.has(slot.panel),
        )
      : [];

  return (
    <div className="flex flex-col gap-3" data-testid="pool-settings-pane">
      {state.phase === "loading" ? (
        <div data-testid="pool-settings-loading">
          <Skeleton shape="title" label={POOL_SETTINGS_LOADING_LABEL} />
          {POOL_SETTINGS_BUILT_SLOTS.map((slot) => (
            <Skeleton key={slot.panel} shape="card" />
          ))}
        </div>
      ) : null}
      {state.phase === "failed" ? (
        <div data-testid="pool-settings-failed">
          <InlineBanner
            variant="warning"
            message={POOL_SETTINGS_FAILED_MESSAGE}
            action={{
              label: POOL_SETTINGS_RETRY_LABEL,
              onAction: () => {
                void pool.refetch();
              },
            }}
          />
        </div>
      ) : null}
      {state.phase === "noAccess" ? (
        <PoolSettingsNoAccess reason={state.reason} onGoBack={onGoBack} />
      ) : null}
      {state.phase === "ready" ? (
        <div className="flex flex-col gap-3" data-testid="pool-settings-slots">
          {slots.map((slot) => (
            <PoolSettingsCardSlot
              key={slot.panel}
              slot={slot}
              isActive={slot.panel === activePanel}
            >
              {slot.panel === "pool" ? (
                <PoolSettingsPoolCard
                  poolId={poolId}
                  poolName={pool.data?.data?.pool.name ?? null}
                  bannerImageUrl={pool.data?.data?.pool.bannerImageUrl ?? null}
                />
              ) : slot.panel === "joining" ? (
                <PoolSettingsJoiningCard
                  poolId={poolId}
                  pool={poolFields}
                  isPoolLoading={pool.isLoading}
                />
              ) : slot.panel === "contest-rules" ? (
                <PoolSettingsRulesCard poolId={poolId} />
              ) : slot.panel === "contests" ? (
                <PoolSettingsContestsCard poolId={poolId} />
              ) : slot.panel === "season" ? (
                <PoolSeasonCardContainer poolId={poolId} />
              ) : slot.panel === "awards" ? (
                <PoolAwardsPanel poolId={poolId} />
              ) : slot.panel === "chat" ? (
                <PoolSettingsChatCard
                  poolId={poolId}
                  poolChatEnabled={pool.data?.data?.pool.chatEnabled}
                />
              ) : slot.panel === "danger-zone" ? (
                <PoolSettingsDangerZoneCard
                  poolId={poolId}
                  activeMemberCount={pool.data?.data?.activeMemberCount ?? null}
                  pendingMemberCount={
                    pool.data?.data?.pendingMemberCount ?? null
                  }
                />
              ) : null}
            </PoolSettingsCardSlot>
          ))}
        </div>
      ) : null}
    </div>
  );
};
